/**
 * RTWO Cryptography & Identity Engine — Provider Bootstrap
 */

import type { IdentityBackendType } from '../adapters/identity-backend-interface';
import type { KeyProviderType } from '../adapters/key-provider-interface';
import {
  createIdentityBackendCatalog,
  createKeyProviderCatalog,
} from '../providers/crypto-provider-catalog';
import { configureSha256HashProvider } from '../providers/crypto-provider-factory';
import type {
  IConfigurationBindingService,
  IProviderLifecycleService,
  IRepositoryBootstrapService,
} from '../service-interfaces';
import type { IdentityBackendRegistry, KeyProviderRegistry } from '../registry/provider-registries';

export interface CryptoProviderBootstrapResult {
  keyProvidersRegistered: number;
  identityBackendsRegistered: number;
  keyProviderTypes: KeyProviderType[];
  identityBackendTypes: IdentityBackendType[];
  source: string;
}

export function registerCryptoProviderCatalog(
  keyProviderRegistry: KeyProviderRegistry,
  identityBackendRegistry: IdentityBackendRegistry
): CryptoProviderBootstrapResult {
  const keyCatalog = createKeyProviderCatalog();
  const identityCatalog = createIdentityBackendCatalog();
  configureSha256HashProvider();

  for (const provider of keyCatalog.values()) {
    keyProviderRegistry.register(provider, { activate: false });
  }

  for (const backend of identityCatalog.values()) {
    identityBackendRegistry.register(backend, { activate: false });
  }

  return {
    keyProvidersRegistered: keyCatalog.size,
    identityBackendsRegistered: identityCatalog.size,
    keyProviderTypes: Array.from(keyCatalog.keys()),
    identityBackendTypes: Array.from(identityCatalog.keys()),
    source: 'Cryptography Provider — Catalog Registered',
  };
}

export async function activateCryptoKeyProvider(
  configurationId: string,
  providerType: KeyProviderType,
  services: {
    configurationBinding: IConfigurationBindingService;
    providerLifecycle: IProviderLifecycleService;
    repositoryBootstrap: IRepositoryBootstrapService;
  }
): Promise<{
  bound: boolean;
  connected: boolean;
  repositoriesBootstrapped: boolean;
  scaffoldOnly: boolean;
}> {
  const bindResult = await services.configurationBinding.bindKeyProvider(
    configurationId,
    providerType
  );

  if (!bindResult.success) {
    return {
      bound: false,
      connected: false,
      repositoriesBootstrapped: false,
      scaffoldOnly: false,
    };
  }

  if (bindResult.scaffoldOnly) {
    return {
      bound: true,
      connected: false,
      repositoriesBootstrapped: false,
      scaffoldOnly: true,
    };
  }

  const status = await services.providerLifecycle.connectKeyProvider();
  const connected = status === 'connected';

  const bootstrapResult = connected
    ? await services.repositoryBootstrap.bootstrapRepositories()
    : { success: false };

  return {
    bound: true,
    connected,
    repositoriesBootstrapped: bootstrapResult.success,
    scaffoldOnly: false,
  };
}
